import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { cn } from "../../../ui/utils/cn";
import { Emoji } from "../../../ui/components/Emoji";
import { MobileSheet } from "../../../ui/components/MobileSheet";
import { useGetEmojiSelectorQuery } from "../api/emojiApi";
import type { SelectorEmoji } from "../types/emojiTypes";
import { categoryIcon, emojiKey, selectorEmojiMatches, useIsNarrow } from "./emojiCatalogShared";

/**
 * EmojiKeyboard — the platform's custom emoji keyboard (iOS-style).
 *
 * Category tabs + search over the FULL registry (~1,900 emojis), rendered one
 * category at a time so the DOM stays light. On mobile it opens as a bottom
 * sheet (the caller blurs its text input first, so the OS keyboard slides away
 * and this one takes its place); on desktop it's a dropdown panel — wrap the
 * trigger in a `relative` container.
 *
 * Pick flow: tap an emoji → `onPick(emoji)` fires and the keyboard closes
 * (unless `stayOpen`) → the caller re-focuses its input (OS keyboard returns).
 *
 * Modes:
 *  - "free"  (comments): every active emoji is pickable — ambient use is free.
 *  - "aotd"  (next-AOTD picker): emojis the user already burned in a generated
 *    AOTD are blocked unless `allowRevive` (an active Reclamation card).
 *  - "pick"  (AOTD guessing): picking someone else's emojis, so none of the
 *    viewer's own marks (staged, burned) show. The caller dims what can't go
 *    in with `blockedReason` and rings what is already placed with `selected`.
 */

export type EmojiKeyboardMode = "free" | "aotd" | "pick";

export interface EmojiKeyboardProps {
  open: boolean;
  onClose: () => void;
  /** fired with the picked emoji (canonical display form) */
  onPick: (emoji: string) => void;
  mode?: EmojiKeyboardMode;
  /** aotd mode: burned emojis become pickable (Reclamation card active) */
  allowRevive?: boolean;
  /** keep the keyboard open after a pick (multi-pick, e.g. AOTD slots) */
  stayOpen?: boolean;
  title?: string;
  className?: string;
  /** pick mode: why this emoji can't be picked (it shows dimmed), or null */
  blockedReason?: (emoji: string) => string | null;
  /** pick mode: emojis already placed, ringed in the grid */
  selected?: string[];
  /** shown above the search, e.g. the slots being filled */
  header?: React.ReactNode;
  /** load the registry while closed, so the first open is instant */
  preload?: boolean;
}

const SEARCH_RENDER_CAP = 240;

const EmojiKeyboard: React.FC<EmojiKeyboardProps> = ({
  open,
  onClose,
  onPick,
  mode = "free",
  allowRevive = false,
  stayOpen = false,
  title = "Pick an emoji",
  className,
  blockedReason,
  selected,
  header,
  preload = false,
}) => {
  const isNarrow = useIsNarrow();
  const { data, isLoading, isError } = useGetEmojiSelectorQuery(undefined, {
    skip: !open && !preload,
  });
  const selectedKeys = useMemo(() => new Set((selected ?? []).map(emojiKey)), [selected]);
  const [notice, setNotice] = useState<string | null>(null);

  const categories = useMemo(() => data?.data?.categories ?? [], [data]);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const gridRef = useRef<HTMLDivElement | null>(null);

  // reset transient state each open
  useEffect(() => {
    if (open) {
      setSearch("");
      setNotice(null);
    }
  }, [open]);

  const currentCat =
    activeCat && categories.some((c) => c.category === activeCat)
      ? activeCat
      : categories[0]?.category ?? null;

  const searching = search.trim().length > 0;

  const visible: Array<{ category: string; emojis: SelectorEmoji[] }> = useMemo(() => {
    if (searching) {
      let budget = SEARCH_RENDER_CAP;
      const out: Array<{ category: string; emojis: SelectorEmoji[] }> = [];
      for (const c of categories) {
        if (budget <= 0) break;
        const hits = c.emojis.filter((e) => selectorEmojiMatches(e, c.category, search));
        if (hits.length > 0) {
          out.push({ category: c.category, emojis: hits.slice(0, budget) });
          budget -= hits.length;
        }
      }
      return out;
    }
    const cat = categories.find((c) => c.category === currentCat);
    return cat ? [cat] : [];
  }, [categories, currentCat, search, searching]);

  const totalMatches = useMemo(
    () =>
      searching
        ? categories.reduce(
            (n, c) => n + c.emojis.filter((e) => selectorEmojiMatches(e, c.category, search)).length,
            0,
          )
        : 0,
    [categories, search, searching],
  );

  const handlePick = (e: SelectorEmoji): void => {
    if (mode === "aotd" && e.used && !allowRevive) {
      toast.info("You've already used this emoji. Play a Reclamation card to use it again.");
      return;
    }
    const blocked = mode === "pick" ? blockedReason?.(e.e) ?? null : null;
    setNotice(blocked);
    if (blocked) return;
    onPick(e.e);
    if (!stayOpen) onClose();
  };

  const switchCat = (category: string): void => {
    setActiveCat(category);
    setSearch("");
    setNotice(null);
    gridRef.current?.scrollTo({ top: 0 });
  };

  // tabs row — pinned at the BOTTOM on mobile (like the iOS keyboard), at the
  // top on desktop. Explicit bg-transparent/appearance-none everywhere: this
  // app has no global button reset, so plain <button>s render the UA gray box.
  const tabs = !searching && (
    <div
      className={cn(
        "flex shrink-0 items-center overflow-x-auto",
        isNarrow ? "justify-around gap-0 border-t border-ink/10 pt-1" : "gap-0.5 pb-0.5",
      )}
    >
      {categories.map((c) => (
        <button
          key={c.category}
          type="button"
          title={c.category}
          onMouseDown={(ev) => ev.preventDefault()}
          onClick={() => switchCat(c.category)}
          className={cn(
            "flex h-9 w-9 shrink-0 appearance-none items-center justify-center rounded-full border-0 bg-transparent text-base transition-colors",
            c.category === currentCat
              ? "bg-ink/10"
              : "opacity-45 hover:bg-ink/5 hover:opacity-90",
          )}
        >
          <Emoji scale={1.1}>{categoryIcon(c.category)}</Emoji>
        </button>
      ))}
    </div>
  );

  const body = (
    <div className={cn("flex h-full min-h-0 flex-col gap-1.5", className)}>
      {header}
      {notice && <div className="shrink-0 text-xs text-pink">{notice}</div>}
      {/* search — never auto-focused (the sheet suppresses Radix auto-focus,
          or the OS keyboard would pop right back over it) */}
      <input
        type="text"
        value={search}
        onChange={(ev) => setSearch(ev.target.value)}
        placeholder="Search emojis…"
        // 16px on mobile keeps iOS Safari from auto-zooming on focus
        className="box-border w-full shrink-0 appearance-none rounded-md border border-ink/15 bg-ink/5 px-3 py-1.5 text-sm focus:outline-none max-[700px]:text-[16px]"
      />

      {!isNarrow && tabs}

      {/* grid */}
      <div ref={gridRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {isLoading && <div className="p-3 text-sm text-gray-500">Loading emojis…</div>}
        {isError && (
          <div className="p-3 text-sm text-pink">Failed to load the emoji registry.</div>
        )}
        {!isLoading &&
          visible.map((c) => (
            <div key={c.category} className="mb-2">
              {searching && (
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {c.category}
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(2.6rem,1fr))]">
                {c.emojis.map((e) => {
                  const picking = mode === "pick";
                  const aotdBlocked = mode === "aotd" && e.used && !allowRevive;
                  const revivePick = mode === "aotd" && e.used && allowRevive;
                  const pickBlocked = picking && !!blockedReason?.(e.e);
                  const tooltip = picking
                    ? e.n ?? c.category
                    : e.mine
                    ? `${e.n ?? "emoji"} — your signature`
                    : e.used
                      ? `${e.n ?? "emoji"} — already used in a past AOTD`
                      : e.reserved
                        ? `${e.n ?? "emoji"} — reserved (${e.reserved.label ?? e.reserved.kind})`
                        : e.n ?? c.category;
                  return (
                    <button
                      key={e.e}
                      type="button"
                      title={tooltip}
                      // preventDefault keeps focus where the caller wants it —
                      // the blur/refocus dance is owned by the composer
                      onMouseDown={(ev) => ev.preventDefault()}
                      onClick={() => handlePick(e)}
                      className={cn(
                        "relative flex h-11 appearance-none items-center justify-center rounded-md border-0 bg-transparent transition-colors",
                        "hover:bg-ink/5 active:bg-ink/10",
                        !picking && e.staged && "bg-sky/20 ring-1 ring-sky",
                        picking && selectedKeys.has(emojiKey(e.e)) && "bg-ink/10 ring-1 ring-ink/40",
                        (aotdBlocked || pickBlocked) && "opacity-25",
                        revivePick && "ring-1 ring-gold",
                      )}
                    >
                      <Emoji scale={1.6}>{e.e}</Emoji>
                      {mode === "aotd" && e.used && (
                        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-gold" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        {!isLoading && searching && totalMatches === 0 && (
          <div className="p-3 text-sm text-gray-500">No emoji match “{search}”.</div>
        )}
        {!isLoading && searching && totalMatches > SEARCH_RENDER_CAP && (
          <div className="p-2 text-center text-xs text-gray-400">
            Showing {SEARCH_RENDER_CAP} of {totalMatches} — keep typing to narrow down.
          </div>
        )}
      </div>

      {isNarrow && tabs}
    </div>
  );

  if (isNarrow) {
    return (
      <MobileSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        // keyboard-like: one ~half-screen snap, flush over the bottom app bar
        snapPoints={[55]}
        fullModal
        showGrabber
        preventAutoFocus
        contentClassName="px-2 pb-1"
      >
        {body}
      </MobileSheet>
    );
  }

  if (!open) return null;
  return (
    <>
      {/* click-away backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        className={cn(
          "absolute left-0 top-full z-50 mt-2 flex h-80 w-[22rem] max-w-[92vw] flex-col",
          "rounded-sm border-brutal border-ink bg-white p-3 shadow-lg",
        )}
      >
        {body}
      </div>
    </>
  );
};

export default EmojiKeyboard;
